import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAlert } from 'react-alert';
import VirtualTryOnButton from './VirtualTryOnButton';
import PhotoUploadModal from './PhotoUploadModal';
import TryOnLoadingOverlay from './TryOnLoadingOverlay';
import TryOnMenuOverlay from './TryOnMenuOverlay';
import { tryOnOutfit, regenerateTryOn } from 'api/virtualTryOnApi';
import { getError } from 'utils/appHelpers';
import { listUserPhotosFromS3 } from 'utils/s3UploadHelper';
import './VirtualTryOn.scss';

/**
 * Virtual Try-On Image Wrapper
 * Wraps product images with virtual try-on functionality
 * Handles image toggle between original and try-on versions
 */
const VirtualTryOnImageWrapper = ({ likiee, children, productImage }) => {
  const alert = useAlert();
  const [isShowingTryOn, setIsShowingTryOn] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [, setIsCached] = useState(false);
  const [, setCreditsCharged] = useState(0);

  // Check if user has uploaded photos
  useEffect(() => {
    loadUserPhotos();
  }, []);

  const loadUserPhotos = async () => {
    try {
      // One-time migration: clear old format photos from localStorage
      const migrationKey = 'virtualTryOn_migrated_v2';
      if (!localStorage.getItem(migrationKey)) {
        localStorage.removeItem('virtualTryOnPhotos');
        localStorage.setItem(migrationKey, 'true');
      }

      // Load from S3
      const photos = await listUserPhotosFromS3();
      if (photos.length > 0) {
        // Use the most recent full-body-front photo or first available
        const preferredPhoto = photos.find(p => p.photoType === 'full-body-front') || photos[0];
        setUserPhoto(preferredPhoto);
      } else {
        // No photos in S3
        setUserPhoto(null);
      }
    } catch (error) {
      console.error('Failed to load user photos:', error);
      // Only fallback to localStorage if migration hasn't run
      const migrationKey = 'virtualTryOn_migrated_v2';
      if (!localStorage.getItem(migrationKey)) {
        const storedPhotos = JSON.parse(localStorage.getItem('virtualTryOnPhotos') || '[]');
        if (storedPhotos.length > 0) {
          setUserPhoto(storedPhotos[0]);
        }
      } else {
        // Migration has run, don't use localStorage
        setUserPhoto(null);
      }
    }
  };

  // Check if this is a fashion product
  const isFashion = likiee?.aiSummary?.imageType === 'fashion';

  const handleTryOnClick = () => {
    if (isShowingTryOn) {
      // If showing try-on, toggle the menu
      setIsMenuOpen(!isMenuOpen);
    } else if (tryOnImage) {
      // Show existing try-on image and open menu
      setIsShowingTryOn(true);
      setIsMenuOpen(true);
    } else if (userPhoto) {
      // Generate new try-on image
      generateTryOn();
    } else {
      // Open upload modal for first-time users
      setIsUploadModalOpen(true);
    }
  };

  const handlePhotoUpload = uploadedPhoto => {
    setUserPhoto(uploadedPhoto);
    // Automatically generate try-on after upload
    generateTryOn(uploadedPhoto);
  };

  const generateTryOn = async (photo = userPhoto) => {
    if (!photo || !likiee) return;

    setIsGenerating(true);
    setIsMenuOpen(false);

    try {
      // Call the virtual try-on API
      const result = await tryOnOutfit(likiee.id, productImage, photo.photoPath);

      // Set the generated image
      setTryOnImage(result.imageUrl);
      setIsCached(result.isCached);
      setCreditsCharged(result.creditsCharged);
      setIsGenerating(false);
      setIsShowingTryOn(true);
      setIsMenuOpen(true);

      // Show message about caching
      if (result.isCached) {
        alert.success('Try-on loaded from cache - Free!');
      } else {
        alert.success(`Try-on generated successfully (${result.creditsCharged} credits used)`);
      }
    } catch (error) {
      console.error('[VirtualTryOn] Generation failed:', error);
      setIsGenerating(false);

      // Handle specific errors
      if (error.message === 'INSUFFICIENT_CREDITS') {
        alert.error('Insufficient credits. Please upgrade to continue.');
        // TODO: Show upgrade modal
      } else if (error.message === 'AUTHENTICATION_REQUIRED') {
        alert.error('Please sign in to use virtual try-on.');
      } else if (error.message === 'INVALID_INPUT') {
        alert.error('Invalid input. Please try uploading your photo again.');
        setIsUploadModalOpen(true);
      } else if (error.message?.startsWith('S3_KEY_NOT_FOUND')) {
        alert.error('Image not found. The product image or your photo may have been deleted.');
        console.error('[VirtualTryOn] S3 key issue:', {
          productImage,
          photoPath: photo.photoPath,
        });
      } else {
        alert.error(getError(error) || 'Try-on generation failed. Please try again.');
      }
    }
  };

  const handleRegenerate = async () => {
    if (!userPhoto || !likiee) return;

    setIsGenerating(true);
    setIsMenuOpen(false);

    try {
      // Use regenerate API (always charges credits)
      const result = await regenerateTryOn(likiee.id, productImage, userPhoto.photoPath);

      setTryOnImage(result.imageUrl);
      setIsCached(false);
      setCreditsCharged(result.creditsCharged);
      setIsGenerating(false);
      setIsShowingTryOn(true);
      setIsMenuOpen(true);

      alert.success(`New try-on generated (${result.creditsCharged} credits used)`);
    } catch (error) {
      console.error('[VirtualTryOn] Regeneration failed:', error);
      setIsGenerating(false);

      if (error.message === 'INSUFFICIENT_CREDITS') {
        alert.error('Insufficient credits. Please upgrade to continue.');
      } else if (error.message?.startsWith('S3_KEY_NOT_FOUND')) {
        alert.error('Image not found. The product image or your photo may have been deleted.');
      } else {
        alert.error(getError(error) || 'Regeneration failed. Please try again.');
      }
    }
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setIsShowingTryOn(false);
  };

  if (!isFashion) {
    // Return children as-is for non-fashion products
    return children;
  }

  return (
    <div className={`virtual-tryon-image-wrapper ${isMenuOpen ? 'menu-is-open' : ''}`}>
      {/* Original product image or try-on result - always visible to maintain layout */}
      {!isShowingTryOn && children}
      {isShowingTryOn && tryOnImage && (
        <div className='virtual-tryon-result'>
          <img src={tryOnImage} alt='Virtual try-on result' className='virtual-tryon-result__image' />
        </div>
      )}

      {/* Loading overlay during generation - overlays on top without hiding content */}
      {isGenerating && <TryOnLoadingOverlay productImage={productImage} />}

      {/* Try-on button and menu (top-right corner) */}
      {!isGenerating && (
        <div className='virtual-tryon-controls'>
          {/* Menu bar (expands from button) */}
          {isShowingTryOn && isMenuOpen && (
            <TryOnMenuOverlay onRegenerate={handleRegenerate} tryOnImage={tryOnImage} onClose={handleCloseMenu} />
          )}

          {/* Try-on button */}
          <VirtualTryOnButton
            likiee={likiee}
            onTryOnClick={handleTryOnClick}
            isShowingTryOn={isShowingTryOn}
            isGenerating={isGenerating}
            isMenuOpen={isMenuOpen}
          />
        </div>
      )}

      {/* Photo upload modal */}
      <PhotoUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onPhotoUpload={handlePhotoUpload}
      />
    </div>
  );
};

VirtualTryOnImageWrapper.propTypes = {
  likiee: PropTypes.shape({
    aiSummary: PropTypes.shape({
      imageType: PropTypes.string,
    }),
  }),
  children: PropTypes.node.isRequired,
  productImage: PropTypes.string,
};

export default VirtualTryOnImageWrapper;
